import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { applyOnboardingPending, pendingTermsAppliesTo } from "@/lib/onboardingPending";

/**
 * Mounted once at the app root. Whenever an account is signed in (Apple,
 * Google, email now or after confirmation, or Sign in to an existing account),
 * the picks, home area, vote and terms held on this device are written to it.
 */
export function useOnboardingPendingReplay() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const uid = user?.id;
  const email = user?.email ?? null;

  useEffect(() => {
    if (!uid) return;
    // The terms line was shown, so the separate Terms pop up must never queue
    // for this sign in, even before the write below lands.
    if (pendingTermsAppliesTo(uid)) {
      queryClient.setQueryData(["terms-acceptance", uid], false);
    }
    void applyOnboardingPending(supabase, { id: uid, email }).then((r) => {
      if (r.picks > 0) {
        queryClient.invalidateQueries({ queryKey: ["favorite-cafes", uid] });
        queryClient.invalidateQueries({ queryKey: ["user-favorites", uid] });
        queryClient.invalidateQueries({ queryKey: ["user-favorites-count", uid] });
        queryClient.invalidateQueries({ queryKey: ["user-favorite-ids", uid] });
      }
      if (r.location) queryClient.invalidateQueries({ queryKey: ["user-preferences"] });
      if (r.terms) queryClient.setQueryData(["terms-acceptance", uid], false);
    });
  }, [uid, email, queryClient]);
}
